
import * as React from "react"

import type { ToastProps, ToastActionElement } from "@/components/ui/toast"

const TOAST_LIMIT = 20
const TOAST_REMOVE_DELAY = 1000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast> & { id: string }
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: string
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: string
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action

      // Dismiss all toasts if toastId is not provided
      if (toastId === undefined) {
        return {
          ...state,
          toasts: state.toasts.map((t) => ({
            ...t,
            open: false,
          })),
        }
      }

      // Dismiss a specific toast
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId ? { ...t, open: false } : t
        ),
      }
    }

    case actionTypes.REMOVE_TOAST: {
      const { toastId } = action

      // Remove all toasts if toastId is not provided
      if (toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }

      // Remove a specific toast
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== toastId),
      }
    }
  }
}

// Define the toast function type for better type safety
type ToastFunction = (props: Omit<ToasterToast, "id">) => string

// Updated context type to include toast function
type ToastContextType = {
  toasts: ToasterToast[]
  addToast: (toast: Omit<ToasterToast, "id">) => string
  updateToast: (toast: Partial<ToasterToast> & { id: string }) => void
  dismissToast: (toastId?: string) => void
  removeToast: (toastId?: string) => void
  toast: ToastFunction
}

const Toast = React.createContext<ToastContextType>({
  toasts: [],
  addToast: () => "",
  updateToast: () => {},
  dismissToast: () => {},
  removeToast: () => {},
  toast: () => "", // Add the toast function to the default value
})

export const useToast = () => {
  const context = React.useContext(Toast)

  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider")
  }

  return context
}

export const ToastProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [state, dispatch] = React.useReducer(reducer, {
    toasts: [],
  })

  React.useEffect(() => {
    state.toasts.forEach((toast) => {
      if (!toast.open) {
        if (toastTimeouts.has(toast.id)) {
          return
        }

        const timeout = setTimeout(() => {
          dispatch({ type: actionTypes.REMOVE_TOAST, toastId: toast.id })
        }, TOAST_REMOVE_DELAY)

        toastTimeouts.set(toast.id, timeout)
      }
    })

    return () => {
      // Clear all timeouts on unmount
      toastTimeouts.forEach((timeout) => {
        clearTimeout(timeout)
      })
      toastTimeouts.clear()
    }
  }, [state.toasts])

  const addToast = React.useCallback(
    (toast: Omit<ToasterToast, "id">) => {
      const id = crypto.randomUUID()

      dispatch({
        type: actionTypes.ADD_TOAST,
        toast: {
          ...toast,
          id,
          open: true,
        },
      })

      return id
    },
    [dispatch]
  )

  const updateToast = React.useCallback(
    (toast: Partial<ToasterToast> & { id: string }) => {
      dispatch({ type: actionTypes.UPDATE_TOAST, toast })
    },
    [dispatch]
  )

  const dismissToast = React.useCallback(
    (toastId?: string) => {
      dispatch({ type: actionTypes.DISMISS_TOAST, toastId })
    },
    [dispatch]
  )

  const removeToast = React.useCallback(
    (toastId?: string) => {
      dispatch({ type: actionTypes.REMOVE_TOAST, toastId })
    },
    [dispatch]
  )

  // Create a toast function that uses addToast
  const toast: ToastFunction = React.useCallback(
    (props) => {
      return addToast(props);
    },
    [addToast]
  )

  // Register global dispatch for use outside React tree
  React.useEffect(() => {
    registerToastDispatch(addToast);
    return () => registerToastDispatch(null);
  }, [addToast]);

  return (
    <Toast.Provider
      value={{
        toasts: state.toasts,
        addToast,
        updateToast,
        dismissToast,
        removeToast,
        toast, // Include the toast function in the context value
      }}
    >
      {children}
    </Toast.Provider>
  )
}

// Global dispatcher for toast calls outside React tree
let globalDispatch: ((toast: Omit<ToasterToast, "id">) => string) | null = null;

export const registerToastDispatch = (dispatch: typeof globalDispatch) => {
  globalDispatch = dispatch;
};

// Safe toast function that works anywhere (including outside React components)
export function toast(props: Omit<ToasterToast, "id">): string {
  if (!globalDispatch) {
    console.warn('[Toast] Called before provider mounted');
    return '';
  }
  return globalDispatch(props);
}

export { type ToastProps, type ToastActionElement }
